// TAPINTO® Editor Frontend - Editor Canvas Component
// Version: 1.0
// Date: 2025-10-01

import React, { useCallback, useEffect } from 'react';
import { Editor, Frame, Element } from '@craftjs/core';
import { useEditorStore } from '../../stores/editorStore';
import { useAssetStore } from '../../stores/assetStore';
import { Toolbar } from './Toolbar';
import { ComponentLibrary } from './ComponentLibrary';
import { PropertiesPanel } from './PropertiesPanel';
import { AssetBrowser } from '../assets/AssetBrowser';
import { VersionHistory } from '../version/VersionHistory';
import { BreakpointSelector } from './BreakpointSelector';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { PageStructure } from '../../types';
import { componentResolver } from './resolver';

interface EditorCanvasProps {
  pageId: string;
}

export function EditorCanvas({ pageId }: EditorCanvasProps) {
  const { 
    currentPage, 
    loadPage, 
    updatePage, 
    savePage, 
    publishPage,
    isDirty, 
    isSaving, 
    isPublishing,
    setDirty,
    setSelectedComponent 
  } = useEditorStore();
  
  const { fetchAssets } = useAssetStore();
  const [showAssetBrowser, setShowAssetBrowser] = React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  // Load page on mount
  useEffect(() => {
    if (pageId && (!currentPage || currentPage.page_id !== pageId)) {
      loadPage(pageId);
    }
  }, [pageId, currentPage, loadPage]);

  // Load assets on mount
  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Handle structure changes
  const handleStructureChange = useCallback(() => {
    if (!currentPage) return;

    setDirty(true);
  }, [currentPage, setDirty]);

  // Handle component selection
  const handleComponentSelect = useCallback((nodeId: string | null) => {
    setSelectedComponent(nodeId);
  }, [setSelectedComponent]);

  // Handle save
  const handleSave = useCallback(async () => {
    await savePage();
  }, [savePage]);

  // Handle publish
  const handlePublish = useCallback(async () => {
    await publishPage();
  }, [publishPage]);

  if (!currentPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold text-neutral-900">
              {currentPage.title}
            </h1>
            <span className="text-sm text-neutral-500">
              /{currentPage.slug}
            </span>
            {isDirty && (
              <span className="text-sm text-amber-600 font-medium">
                • Unsaved changes
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <BreakpointSelector
              value={currentBreakpoint}
              onChange={setCurrentBreakpoint}
            />
            
            <button
              onClick={() => setShowVersionHistory(true)}
              className="btn btn-outline btn-sm"
            >
              History
            </button>
            
            <button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="btn btn-secondary btn-sm"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            
            <button
              onClick={handlePublish}
              disabled={isPublishing}
              className="btn btn-primary btn-sm"
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Component Library */}
        <div className="w-80 bg-white border-r border-neutral-200 flex flex-col">
          <ComponentLibrary />
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 flex flex-col">
          <Toolbar />
          
          <div className="flex-1 overflow-auto p-4">
            <div className="max-w-6xl mx-auto">
              <div 
                className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden"
                style={{
                  width: currentBreakpoint === 'mobile' ? '375px' : 
                         currentBreakpoint === 'tablet' ? '768px' : '100%',
                  margin: '0 auto'
                }}
              >
                <Editor
                  resolver={componentResolver}
                  onNodesChange={handleStructureChange}
                >
                  <Frame>
                    <Element is="div" canvas>
                      {/* Page content will be rendered here */}
                    </Element>
                  </Frame>
                </Editor>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties Panel */}
        <div className="w-80 bg-white border-l border-neutral-200">
          <PropertiesPanel />
        </div>
      </div>

      {/* Asset Browser Modal */}
      {showAssetBrowser && (
        <AssetBrowser
          isOpen={showAssetBrowser}
          onClose={() => setShowAssetBrowser(false)}
          onSelect={(asset) => {
            // Handle asset selection
            setShowAssetBrowser(false);
          }}
        />
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <VersionHistory
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          pageId={pageId}
        />
      )}
    </div>
  );
}
